import styled from "@emotion/styled"
import { useRef, useState } from "react"
import { Groups, MatchedStatuses, PriorityData, ProjectPage, useOutsideClick } from "utils"
import { RelatedProjects } from "utils/utils"
import { ProjectsMessages } from "../../Projects"
import { Backdrop, StickyCTAWrap } from "../../StandardProject"
import StickyCTA from "../../StickyCTA"
import Apartments from "../Apartments"
import Gallery from "../Gallery"
import ContactMeetingSchedule from "./ContactMeetingSchedule"
import CustomerReviews from "./CustomerReviews"
import DrawerContactForm from "./DrawerContactForm"
import HeroContent from "./HeroContent"
import MapContainer from "./MapContainer"
import NewRelatedGallery from "./NewRelatedGallery"
import ProjectAdvantages from "./ProjectAdvantages"
import ProjectBenefits from "./ProjectBenefits"
import ProjectVisualization from "./ProjectVisualization"
import RentFAQ from "./RentFAQ"

const StyledContainer = styled.div`
  font-family: var(--narkis), var(--rag), sans-serif !important;
  * {
    font-family: inherit !important;
  }
`

type Props = {
  projectData: ProjectPage
  statuses: MatchedStatuses
  groups: Groups
  designations: string[]
  cities: string[]
  countries: string[]
  locale: string
  formSubjects: Record<string, string>
  messages: ProjectsMessages
  relatedProjects?: RelatedProjects
  priorityData?: PriorityData
  toLobby?: string
  propertyTypes: {
    id: number
    value: string
  }[]
  narkisCondensedVariable?: string
  narkisVariable?: string
  is4Rent?: boolean
  allRentalProjects?: { name: string; id: number; rooms: string[] }[]
  cmsProjectLeadIds?: Record<string, number>
}

function ApartmentMarketingForRent({
  projectData,
  locale,
  cities,
  countries,
  formSubjects,
  messages,
  relatedProjects,
  priorityData,
  narkisVariable,
  narkisCondensedVariable,
  is4Rent,
  allRentalProjects,
  cmsProjectLeadIds,
}: Props) {
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false)
  const [isBackdropVisible, setIsBackdropVisible] = useState(false)
  const [isDropDownOpen, setIsDropDownOpen] = useState(false)
  const stickyRef = useRef(null)
  const formRef = useRef(null)
  const handleBackdropClose = () => {
    setDrawerOpen(false)
    setIsBackdropVisible(false)
    setIsDropDownOpen(false)
  }
  useOutsideClick([stickyRef, formRef], handleBackdropClose)
  const { gallery } = projectData

  return (
    <StyledContainer className={`${narkisVariable} ${narkisCondensedVariable}`}>
      {projectData?.rentProjectHeroBanner?.title && (
        <HeroContent
          locale={locale}
          city={cities[0]}
          country={countries[0]}
          projectData={projectData}
        />
      )}
      {projectData?.rentProjectVisualization && (
        <ProjectVisualization locale={locale} data={projectData.rentProjectVisualization} />
      )}
      {projectData?.rentProjectBenefits && (
        <ProjectBenefits locale={locale} data={projectData.rentProjectBenefits} />
      )}
      {priorityData ? (
        <Apartments
          projectData={projectData}
          priorityData={priorityData}
          locale={locale}
          formSubjects={formSubjects}
          messages={messages}
          allApChar={priorityData.allApChar}
          allBuChar={priorityData.allBuChar}
          companyId={projectData.companyId}
          useDrawerLayout
          fontFamilyVariable={`${narkisVariable} ${narkisCondensedVariable}`}
          rentProjectInterestedNum={projectData.rentProjectInterestedNum}
          projectFilterId={projectData.projectTitle}
          is4Rent={is4Rent}
          allRentalProjects={allRentalProjects}
          cmsProjectLeadIds={cmsProjectLeadIds}
          noAvailableApartmentsBanner={projectData.noAvailableApartmentsBanner}
          onEmptyStateCta={() => setDrawerOpen(true)}
          leadFormEnabled={!projectData.hideLeadForm}
        />
      ) : null}
      <Gallery
        gallery={gallery}
        locale={locale}
        header={
          projectData?.galleryTitle
            ? {
              title: projectData.galleryTitle,
              subTitle: projectData?.gallerySubTitle,
            }
            : undefined
        }
        style={{
          paddingBlock: "60px",
          paddingLeft: 4,
          paddingRight: 4,
        }}
        swiperBorderRadius={12}
        swiperContainerProps={{ padding: 20, width: 1240 }}
        newStylePagination
      />
      {projectData?.rentProjectAdvantages && (
        <ProjectAdvantages locale={locale} data={projectData.rentProjectAdvantages} />
      )}
      {projectData?.rentProjectJoinBlock && (
        <ContactMeetingSchedule
          locale={locale}
          data={projectData.rentProjectJoinBlock}
          bmbyMeetingId={projectData.bmbyMeetingId}
        />
      )}
      {projectData?.customerReviews && (
        <CustomerReviews data={projectData.customerReviews} locale={locale} />
      )}
      <MapContainer projectData={projectData} locale={locale} />
      {projectData?.rentProjectFAQ && <RentFAQ data={projectData.rentProjectFAQ} locale={locale} />}
      {relatedProjects && relatedProjects?.relatedProjects?.length > 0 && (
        <NewRelatedGallery
          projectData={projectData}
          locale={locale}
          relatedObject={relatedProjects}
          toProject={messages.toProject}
        />
      )}
      {isBackdropVisible && <Backdrop />}

      {!projectData.hideLeadForm && (
        <StickyCTAWrap ref={stickyRef} z={drawerOpen ? 90 : 100} className={"newStyle"}>
          <StickyCTA
            projectType={projectData.projectType}
            messages={messages}
            setIsBackdropVisible={setIsBackdropVisible}
            open={isDropDownOpen}
            setIsFormVisible={setDrawerOpen}
            setIsDropDownOpen={setIsDropDownOpen}
            phone={projectData.phone}
            phoneName={projectData.phoneName}
            miniSite={projectData.miniSite}
            bmbyMeetingId={projectData.bmbyMeetingId}
            virtualTourLink={projectData.virtualTourLink}
            virtualTourLinkName={projectData.virtualTourLinkName}
            isForMarketingRentPage
            locale={locale}
            rentProjectContactText={projectData.rentProjectContactText}
            hideLeadForm={projectData.hideLeadForm}
          />
        </StickyCTAWrap>
      )}
      <DrawerContactForm
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        formProps={{
          messages,
          formSubjects,
          onClose: handleBackdropClose,
          bmbyId: projectData?.leadFormBMBYId,
          status: projectData?.projectStatuses[0]?.name,
          companyId: projectData?.companyId,
          emailForLead: projectData?.emailForLead,
          newStyle: true,
          type: "contact-marketing-4-rent",
        }}
      />
    </StyledContainer>
  )
}

export default ApartmentMarketingForRent
